<template>
  <BittsModal
    container-class="c-population-modal-two-container"
    :show-close-button="false"
    :width="1416"
    :action-loading="isSaving"
    :loading="isInitializing"
    @closed="closeModal"
  >
    <template #header>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center gap-8">
          <h1 class="text-neutral-text-strong font-bold mr-8 text-lg">
            {{ modalTitle }}
          </h1>
          <BittsTag
            v-if="populationTypeTag"
            size="small"
            color="neutral"
            :text="populationTypeTag"
          />
          <BittsTag
            v-if="isOpenData"
            size="small"
            color="info"
            text="Open Data"
          />
        </div>
        <BittsButton
          :center-icon="['fak', 'x']"
          @click="closeModal"
          variant="ghost"
          type="neutral"
          aria-label="Close"
        />
      </div>
    </template>
    <div class="c-population-modal-two__sections-container">
      <div
        class="c-population-modal-two__left-section"
        :class="
          isExpandedPreview
            ? 'c-population-modal-two__left-section--collapsed'
            : ''
        "
      >
        <PopulationDetailTabs
          ref="populationDetailTabsRef"
          v-model:population-name="populationName"
          v-model:population-description="populationDescription"
          v-model:selected-feed-id="selectedFeedId"
          v-model:selected-object="selectedObject"
          v-model:selected-pop-type="selectedPopType"
          v-model:advanced-filter-groups="advancedFilterGroups"
          :mode="mode"
          :is-custom-population="isCustomPopulation"
          :is-offline-partner="isOfflinePartner"
          :is-open-data="isOpenData"
          :original-population-name="population?.name"
          :selected-feed="selectedFeed"
          :is-source-locked="false"
          :population="population"
          :filter-config="filterConfig"
          :alert-config="alertConfig"
          :data-source-config="dataSourceConfig"
          @feed-change="onFeedChange"
          @continue="handleContinue"
          @apply-filters="refreshPreview"
          @add-filter-group="
            (value: boolean) =>
              handleAddFilterGroup(value, selectedFeed, completeFilterSources)
          "
          @delete-filter-group="handleDeleteFilterGroup"
          @change-filter-group-list="handleChangeFilterGroupList"
        />
      </div>
      <div class="c-population-modal-two__right-section">
        <PopulationModalPreviewSection
          :has-completed-details="hasCompletedDetails"
          :pop-count-loaded="popCountLoaded"
          :population-preview-count="populationPreviewCount"
          :has-invalid-filters="effectiveHasInvalidFilters"
          :preview-key="previewKey"
          :population="previewPopulation"
          :source-filter-expressions="effectiveSourceFilterExpressions"
          :offline-partner-uuid="offlinePartnerUuid"
          :is-expanded="isExpandedPreview"
          :is-syncing="selectedFeed?.is_syncing ?? false"
          :is-open-data="isOpenData"
          @refresh="refreshPreview"
          @toggle-expand="isExpandedPreview = !isExpandedPreview"
          @preview-population-count="handlePreviewCount"
          @user-refresh-preview="handleUserRefreshPreview"
        />
      </div>
    </div>
    <template #footer>
      <div v-if="!isViewMode" class="flex items-center w-full">
        <BittsButton
          v-if="isEditMode"
          text="Delete Population"
          type="danger"
          variant="ghost"
          size="large"
          :left-icon="['fak', 'delete']"
          @click="showDeleteModal = true"
        />
        <div class="flex items-center ml-auto gap-8">
          <BittsButton
            text="Cancel"
            type="neutral"
            variant="outline"
            size="large"
            @click="closeModal"
          />
          <BittsButton
            :text="isEditMode ? 'Save changes' : 'Create population'"
            size="large"
            :disabled="isSaveDisabled"
            @click="savePopulation"
          />
        </div>
      </div>
      <div
        v-else
        class="flex items-center w-full"
        :class="isOpenData ? 'justify-end' : 'justify-between'"
      >
        <BittsButton
          v-if="!isOpenData"
          text="Edit"
          type="neutral"
          variant="outline"
          :left-icon="['fak', 'edit']"
          size="large"
          @click="navigateToEditPopulation"
        />
        <BittsButton
          text="Close"
          type="neutral"
          variant="outline"
          size="large"
          @click="closeModal"
        />
      </div>
    </template>
  </BittsModal>
  <BittsModal
    v-if="showConfirmModal"
    variant="confirm"
    title="Are you sure you want to include all your accounts?"
    description="You haven't applied any filters to define this population. All synced accounts will be included"
    primary-button-text="Create Anyway"
    secondary-button-text="Go Back"
    container-class="c-population-modal__confirm-modal"
    @action="savePopulation"
    @closed="showConfirmModal = false"
  >
    <template #image>
      <FontAwesomeIcon
        :icon="['fak', 'warning']"
        style="height: 48px; width: 48px; color: currentColor"
        class="text-warning-accent mb-16"
      />
    </template>
  </BittsModal>
  <DeletePopulationModal
    v-if="showDeleteModal"
    :visible="showDeleteModal"
    :population-id="population?.id"
    :offline-partner-org-id="
      isOfflinePartner ? (route.params.partner_org_id as string) : undefined
    "
    @close="showDeleteModal = false"
    @deleted="closeModal"
  />
  <RouterView />
</template>

<script setup lang="ts">
import { BittsButton, BittsModal, BittsTag } from '@crossbeam/bitts';

import { capitalize } from 'lodash';
import { computed, onMounted, ref, watch } from 'vue';

import type { FilterGroupOutput } from '@/components/filters/types';

import useOfflinePartner from '@/composables/useOfflinePartner';
import { usePopulationDataProvider } from '@/composables/usePopulationDataProvider';
import { usePopulationFilters } from '@/composables/usePopulationFilters';
import { usePopulationForm } from '@/composables/usePopulationForm';
import { usePopulationManagement } from '@/composables/usePopulationManagement';
import { usePopulationModalAlerts } from '@/composables/usePopulationModalAlerts';
import {
  pickDefaultOfflineFeed,
  usePopulationModalState,
} from '@/composables/usePopulationModalState';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import {
  useDataSharesStore,
  useFlashesStore,
  usePopulationsStore,
} from '@/stores';
import {
  OPEN_OPPS_STANDARD_TYPE,
  Population,
  type PopulationAlertConfig,
  type PopulationDataSourceConfig,
  type PopulationFilterConfig,
  SEGMENTS_TO_LABEL_MAP,
  type SegmentType,
  StandardPopulationType,
  isOpenDataPopulation,
} from '@/types/populations';

import DeletePopulationModal from '../DeletePopulationModal.vue';

import PopulationDetailTabs, {
  type PopulationMode,
} from './PopulationDetailTabs.vue';
import {
  advancedFiltersToInternal,
  advancedFiltersToSourceExpressions,
  areAllFiltersComplete,
  internalFiltersToAdvanced,
  populationFiltersToAdvanced,
} from './populationFilterConverters';
import PopulationModalPreviewSection from './PopulationModalPreviewSection.vue';

const emit = defineEmits(['closed']);

const populationsStore = usePopulationsStore();
const dataSharesStore = useDataSharesStore();
const flashesStore = useFlashesStore();

const {
  route,
  router,
  isOfflinePartner,
  offlinePartnerUuid,
  population,
  isEditMode,
  isViewMode,
  isOnboarding,
  navigateAfterClose,
  initializePopulationModal,
} = usePopulationModalState();

const isSaving = ref(false);
const isInitializing = ref(isEditMode.value);
const showDeleteModal = ref(false);

const {
  filterGroupList,
  completeFilterSources,
  hasFiltersChanged,
  handleAddFilterGroup,
  handleDeleteFilterGroup,
  handleChangeFilterGroupList,
  fetchFilterData,
  initializeFilterGroups,
  fallbackSuggestion,
  hasRecommendedFiltersApplied,
  fetchAndSetRecommendedFilters,
} = usePopulationFilters();

const {
  showConfirmModal,
  populationPreviewCount,
  popCountLoaded,
  createPreviewPopulation,
  handlePreviewCount,
  resetPreviewState,
  savePopulation: savePopulationFromComposable,
} = usePopulationManagement();

const { refreshOfflineData, setOfflinePopulationDataShare } =
  useOfflinePartner();

const isExpandedPreview = ref(false);
const hasCompletedDetails = ref(false);
const hasLoadedRecommendedFilters = ref(false);
const hasInitializedAdvancedFilters = ref(false);
const previewKey = ref(0);
const advancedFilterGroups = ref<FilterGroupOutput[]>([]);
const originalAdvancedFilterGroups = ref<FilterGroupOutput[]>([]);

const effectiveFilterGroupList = computed(() => {
  return advancedFiltersToInternal(advancedFilterGroups.value);
});

const effectiveSourceFilterExpressions = computed(() =>
  advancedFiltersToSourceExpressions(advancedFilterGroups.value),
);

const effectiveHasInvalidFilters = computed(() => {
  return !areAllFiltersComplete(advancedFilterGroups.value);
});
const populationDetailTabsRef = ref<InstanceType<
  typeof PopulationDetailTabs
> | null>(null);

const capitalizedPopulation = computed(() => {
  if (standardType.value === OPEN_OPPS_STANDARD_TYPE)
    return 'Open Opportunities';
  return capitalize(standardType.value || 'Custom');
});

const mode = computed<PopulationMode>(() => {
  if (isViewMode.value) return 'view';
  return isEditMode.value ? 'edit' : 'create';
});

const modalTitle = computed(() => {
  const action = isViewMode.value
    ? 'View'
    : isEditMode.value
      ? 'Edit'
      : 'Create';
  const baseTitle = `${action} ${capitalizedPopulation.value} Population`;

  const shouldShowPopulationName =
    isCustomPopulation.value &&
    populationName.value &&
    populationName.value.trim();

  if (shouldShowPopulationName) {
    return `${baseTitle} - ${populationName.value}`;
  }

  return baseTitle;
});

const previewPopulation = computed(() => {
  return createPreviewPopulation(
    population.value,
    selectedFeed.value || null,
    selectedObject.value,
    currentSources.value,
  );
});

function navigateToEditPopulation() {
  if (isOfflinePartner.value) {
    router.push({
      name: 'offline-edit-population',
      params: { population_id: population.value?.id },
    });
  } else {
    router.push({
      name: 'edit_population',
      params: { population_id: population.value?.id },
    });
  }
}

const {
  currentFeeds,
  currentSources,
  currentUploadNames,
  getFeedById,
  getSourcesByFeedId,
  initializeData,
} = usePopulationDataProvider({
  isOfflinePartner,
  offlinePartnerUuid,
});

const standardType = computed(() => {
  if (isEditMode.value) {
    return population.value?.standard_type || null;
  }

  const queryStandardType = route.query.standardType;
  if (!queryStandardType) {
    return null;
  }

  return (queryStandardType as StandardPopulationType) || null;
});

const isCustomPopulation = computed(() => !standardType.value);
const isOpenData = computed(() => isOpenDataPopulation(population.value));

const populationTypeTag = computed(() => {
  if (!isCustomPopulation.value) return null;
  const segment = population.value?.segment as SegmentType | undefined;
  if (!segment) return null;
  return SEGMENTS_TO_LABEL_MAP[segment] ?? null;
});

const {
  selectedFeedId,
  selectedObject,
  populationDescription,
  populationName,
  selectedPopType,
  hasDetailsChanges,
} = usePopulationForm({
  population,
  isEditMode,
});

const selectedFeed = computed(() =>
  selectedFeedId.value && typeof selectedFeedId.value === 'number'
    ? getFeedById(selectedFeedId.value) || null
    : null,
);

const hasAdvancedFiltersChanged = computed(() => {
  const current = advancedFilterGroups.value;
  const original = originalAdvancedFilterGroups.value;

  if (!current.length && !original.length) return false;

  return JSON.stringify(current) !== JSON.stringify(original);
});

const isAdvancedFilterGroupsEmpty = computed(() => {
  if (!advancedFilterGroups.value.length) return true;

  return !advancedFilterGroups.value.some((group) =>
    group.filters?.some((f) => f.value && f.operator),
  );
});

const isFiltersEmpty = computed(() => {
  return isAdvancedFilterGroupsEmpty.value;
});

const {
  populationFiltersAlertVisibility,
  populationFiltersAlertContent,
  handleUserRefreshPreview,
  resetRefreshPreviewFlag,
} = usePopulationModalAlerts({
  isCustomPopulation,
  isEdit: isEditMode,
  selectedFeed,
  isFiltersEmpty,
  hasRecommendedFiltersApplied,
  capitalizedPopulation,
  fallbackSuggestion: fallbackSuggestion(standardType.value),
  advancedFilterGroups,
});

const filterConfig = computed<PopulationFilterConfig>(() => ({
  filterGroupList: effectiveFilterGroupList.value,
  completeFilterSources: completeFilterSources.value,
  hasInvalidFilters: effectiveHasInvalidFilters.value,
  hasFiltersChanged: hasFiltersChanged.value,
  offlinePartnerUuid: offlinePartnerUuid.value,
}));

const alertConfig = computed<PopulationAlertConfig>(() => ({
  populationFiltersAlertVisibility: populationFiltersAlertVisibility.value,
  populationFiltersAlertContent: populationFiltersAlertContent.value,
}));

const dataSourceConfig = computed<PopulationDataSourceConfig>(() => ({
  currentFeeds: currentFeeds.value,
  currentSources: currentSources.value,
  currentUploadNames: currentUploadNames.value,
}));

const hasAnyChanges = computed(() => {
  return hasDetailsChanges.value || hasAdvancedFiltersChanged.value;
});

const hasIncompleteAdvancedFilters = computed(() => {
  return !areAllFiltersComplete(advancedFilterGroups.value);
});

const isSaveDisabled = computed(() => {
  if (hasIncompleteAdvancedFilters.value) return true;

  if (!isEditMode.value) {
    return !populationDetailTabsRef.value?.isDetailsFormValid;
  }
  return !hasAnyChanges.value;
});

watch(
  () => population.value,
  (newPopulation) => {
    if (
      newPopulation &&
      !(newPopulation as Population & { isEmpty?: boolean }).isEmpty
    ) {
      hasCompletedDetails.value = true;
    }
  },
  { immediate: true },
);

watch(
  [() => population.value, completeFilterSources],
  ([pop, sources]) => {
    if (
      (isEditMode.value || isViewMode.value) &&
      pop?.source_filter_expressions &&
      sources.length > 0 &&
      !hasInitializedAdvancedFilters.value
    ) {
      hasInitializedAdvancedFilters.value = true;
      const converted = populationFiltersToAdvanced(
        pop.source_filter_expressions,
      );
      advancedFilterGroups.value = converted;
      originalAdvancedFilterGroups.value = JSON.parse(
        JSON.stringify(converted),
      );
    }
  },
  { immediate: true },
);

watch(selectedObject, (newValue, oldValue) => {
  if (selectedFeedId.value && typeof selectedFeedId.value === 'number') {
    completeFilterSources.value = getFilteredSources(selectedFeedId.value);
  }

  if (mode.value === 'create' && oldValue !== null && newValue !== oldValue) {
    resetCreateModeState();
  }
});

watch(selectedFeedId, (newValue, oldValue) => {
  if (
    mode.value === 'create' &&
    oldValue !== null &&
    oldValue !== 'add-new-source' &&
    newValue !== oldValue
  ) {
    resetCreateModeState();
  }
});

function resetCreateModeState() {
  hasCompletedDetails.value = false;
  hasLoadedRecommendedFilters.value = false;
  hasInitializedAdvancedFilters.value = false;
  advancedFilterGroups.value = [];
  originalAdvancedFilterGroups.value = [];
  handleChangeFilterGroupList([
    {
      filter_expression: [],
      filter_parts: [],
      tableName: '',
      mdmType: '',
    },
  ]);
  previewKey.value += 1;
}

watch(
  [currentFeeds, isOfflinePartner],
  () => {
    if (
      isOfflinePartner.value &&
      currentFeeds.value.length > 0 &&
      !selectedFeedId.value
    ) {
      const defaultFeed = pickDefaultOfflineFeed(currentFeeds.value);
      if (defaultFeed) {
        selectedFeedId.value = defaultFeed.id;
        completeFilterSources.value = getFilteredSources(defaultFeed.id);
      }
    }
  },
  { immediate: true },
);

function getFilteredSources(feedId: number) {
  let sources = getSourcesByFeedId(feedId).filter(
    (source) => source.sync_enabled,
  );

  const feed = getFeedById(feedId);
  const isCsvOrSheets =
    feed?.integration?.type === FILE_UPLOAD_DATA_SOURCE_TYPE ||
    feed?.integration?.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE;

  if (isCsvOrSheets && selectedObject.value) {
    sources = sources.filter((source) => source.id === selectedObject.value);
  }

  return sources;
}

function onFeedChange(feedId: number | 'add-new-source') {
  if (feedId === 'add-new-source') {
    router.push({ name: 'data-sources' });
    return;
  }

  selectedFeedId.value = feedId;
  completeFilterSources.value = getFilteredSources(feedId);
}

function refreshPreview() {
  resetPreviewState();
  previewKey.value += 1;
}

async function handleContinue() {
  if (!isEditMode.value && !isViewMode.value && selectedObject.value) {
    await fetchFilterData(
      { source_id: selectedObject.value } as Parameters<
        typeof fetchFilterData
      >[0],
      currentSources.value,
      offlinePartnerUuid.value,
    );
  }

  if (
    !hasLoadedRecommendedFilters.value &&
    !isCustomPopulation.value &&
    !isOfflinePartner.value &&
    !isEditMode.value &&
    !isViewMode.value &&
    selectedObject.value
  ) {
    hasLoadedRecommendedFilters.value = true;
    await fetchAndSetRecommendedFilters(
      selectedObject.value as number,
      standardType.value as StandardPopulationType,
    );

    const converted = internalFiltersToAdvanced(filterGroupList.value);
    advancedFilterGroups.value = converted;
    originalAdvancedFilterGroups.value = JSON.parse(JSON.stringify(converted));
  }

  hasCompletedDetails.value = true;
}

async function closeModal() {
  await navigateAfterClose();
  emit('closed');
}

async function savePopulation() {
  const filtersChanged = hasAdvancedFiltersChanged.value;

  if (
    !showConfirmModal.value &&
    isFiltersEmpty.value &&
    (!isEditMode.value || filtersChanged)
  ) {
    showConfirmModal.value = true;
    return;
  }

  if (showConfirmModal.value) {
    showConfirmModal.value = false;
  }

  isSaving.value = true;
  const previewCount = populationPreviewCount.value;
  const hasPreviewCount = popCountLoaded.value;
  const editingPopulationId = population.value?.id;

  try {
    const savedPopulation: Population | undefined =
      await savePopulationFromComposable(
        isEditMode.value,
        population.value ||
          ({ standard_type: standardType.value } as Population & {
            standard_type?: StandardPopulationType;
          }),
        typeof selectedFeedId.value === 'number' ? selectedFeedId.value : null,
        selectedObject.value,
        isCustomPopulation.value,
        populationName.value,
        populationDescription.value || null,
        selectedPopType.value,
        standardType.value,
        effectiveSourceFilterExpressions.value,
        capitalizedPopulation.value,
        offlinePartnerUuid.value,
      );

    const partnerUuid = offlinePartnerUuid.value;
    if (isOfflinePartner.value && partnerUuid && savedPopulation) {
      if (!isEditMode.value && savedPopulation.id && selectedObject.value) {
        await setOfflinePopulationDataShare(
          savedPopulation.id as number,
          selectedObject.value,
          partnerUuid,
        );
        await dataSharesStore.refreshDataSharesStore();
      }
      await refreshOfflineData(partnerUuid);
    }

    const populationId = editingPopulationId ?? savedPopulation?.id;

    await closeModal();
    isSaving.value = false;

    const displayName = isCustomPopulation.value
      ? populationName.value
      : capitalizedPopulation.value;
    const action = isEditMode.value ? 'updated' : 'created';
    flashesStore.addSuccessFlash({
      message: `"${displayName}" population has been ${action} successfully`,
    });

    await populationsStore.refreshPopulationsStore();

    if (populationId && hasPreviewCount) {
      populationsStore.setPopulationCount(populationId as number, previewCount);
    }

    if (
      !isEditMode.value &&
      savedPopulation &&
      savedPopulation.id &&
      !isOfflinePartner.value &&
      !isOnboarding.value
    ) {
      await router.push({
        name: 'populations.sharing',
        params: { population_id: savedPopulation.id as number },
        query: {
          ...route.query,
          toEdit: 'true',
          newPopulation: 'true',
        },
      });
    }
  } catch (error) {
    isSaving.value = false;
    throw error;
  }
}

onMounted(async () => {
  await initializePopulationModal({
    isInitializing,
    mode,
    initializeData,
    resetRefreshPreviewFlag,
    selectedFeedId,
    currentFeeds,
    completeFilterSources,
    getFilteredSources,
    currentSources,
    fetchFilterData,
    initializeFilterGroups,
  });
});
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";
:deep(.c-population-modal-two-container) {
  @apply overflow-hidden! flex! flex-col!;
  max-height: calc(100% - 24px) !important;
  height: calc(100% - 24px) !important;
  .o-bitts-modal__content-wrapper {
    @apply min-h-0! flex-1! flex! flex-col! overflow-hidden!;
    flex-basis: 0;
  }
  .o-bitts-modal__header {
    @apply flex! justify-between! items-center! p-16! border-b! border-neutral-border! bg-white! sticky! top-0! z-20! flex-none!;
  }
  .o-bitts-modal__content {
    @apply m-0! p-0! flex! flex-col! overflow-hidden! min-h-0! flex-1!;
    flex-basis: 0;
  }
  .o-bitts-modal__content--background {
    @apply flex! overflow-hidden! min-h-0! flex-1!;
  }
  .o-bitts-modal__footer {
    @apply sticky! bottom-0! z-20! bg-white! border-t! border-neutral-border! p-12! pb-0! mx-0! gap-8! flex-none!;
  }
  .o-bitts-modal__footer::after {
    @apply absolute top-full left-0 right-0 h-16 bg-white;
    content: '';
  }
}

.c-population-modal-two__sections-container {
  @apply flex w-full min-h-0 flex-1 overflow-hidden;
  flex-basis: 0;
}

.c-population-modal-two__left-section {
  @apply flex flex-col border-r border-neutral-border overflow-y-auto min-h-0;
  flex: 0 0 50%;
  position: relative;
  transition:
    width 500ms ease-in-out,
    opacity 300ms ease-in-out 350ms,
    flex-basis 500ms ease-in-out;
}

.c-population-modal-two__left-section--collapsed {
  @apply w-0 border-r-0 opacity-0 overflow-hidden;
  flex: 0 0 0;
  min-width: 0;
  transition:
    width 500ms ease-in-out,
    opacity 150ms ease-in-out,
    flex-basis 500ms ease-in-out;
}

.c-population-modal-two__right-section {
  @apply flex flex-col overflow-y-auto min-h-0 flex-1;
  flex-basis: 0;
}

/* Safari-specific flexbox fix - use height: 0 instead of flex-basis: 0 */
@supports (-webkit-touch-callout: none) {
  :deep(.c-population-modal-two-container) {
    .o-bitts-modal__content-wrapper {
      flex-basis: auto;
      height: 0;
    }
    .o-bitts-modal__content {
      flex-basis: auto;
      height: 0;
    }
  }

  .c-population-modal-two__sections-container {
    flex-basis: auto;
    height: 0;
  }

  .c-population-modal-two__left-section {
    /* Preserve the 50% width - don't override flex-basis */
    height: 0;
  }

  .c-population-modal-two__right-section {
    flex-basis: auto;
    height: 0;
  }
}
</style>
